import React, { useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"
import dayjs from "dayjs"
import { v4 as uuidv4 } from "uuid"

import { useLazyGetContractorsQuery } from "store/api/contractorsApi"
import { useLazyCreateOrderQuery } from "store/api/vendorApi"
import { useLazyGetWarehouseListQuery } from "store/api/warehousesApi"
import { UITypes } from "types"
import { ReactComponent as IconDelete } from "assets/svg/icon-delete.svg"
import { ReactComponent as IconNewOrder } from "assets/svg/modal-icon-new-order.svg"

import getErrorMessage from "utils/getErrorMessage"
import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"
import { ButtonNew } from "components/form/ButtonNew"
import { InputNumber } from "components/form/InputNumber"
import { SelectDynamic } from "components/form/SelectDynamic"

import styles from "./index.module.scss"

interface Props {
  onCancel: () => void
  onSuccess: () => void
}

export const NewVendorOrderModalOld = ({ onCancel, onSuccess }: Props) => {
  const [triggerCreateOrder, { isFetching }] = useLazyCreateOrderQuery()

  const [selectedContractor, setSelectedContractor] = useState<undefined | UITypes.Option[]>(undefined)
  const [selectedWarehouse, setSelectedWarehouse] = useState<undefined | UITypes.Option[]>(undefined)
  const [quantity, setQuantity] = useState<number[]>([1])

  const onInputChange = (index: number, value: number) => {
    const temp = [...quantity]
    temp[index] = value
    setQuantity(temp)
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!selectedContractor) toast.error("Please select contractor")
    else if (!selectedWarehouse) toast.error("Please select warehouse")
    else {
      try {
        const res = await Promise.all(
          quantity.map(async value => {
            const res = await triggerCreateOrder(
              {
                quantity: value,
                contractor_id: Number(selectedContractor[0].value),
                warehouse_id: Number(selectedWarehouse[0].value),
                temp: Math.floor(Math.random() * 1000).toString(),
              },
              false,
            )
            const error = getErrorMessage(res?.error)

            if (error) {
              toast.error(error)
              return false
            }
            return true
          }),
        )

        if (!res.some(value => value === false)) {
          toast.success("Order has been successfully added!")
          onSuccess()
        }
      } catch (error) {
        console.error(error)
      }
    }
  }

  // useEffect(() => {
  //   triggerGetContractors({})
  // }, [])

  return (
    <>
      {createPortal(
        <ModalLayout
          width="570px"
          onConfirm={onSubmit}
          onCancel={onCancel}
          isFetching={isFetching}
          confirmButtonText="Save"
        >
          <ModalHeader title="New order" subtitle="Please input the number of Nuve Thermostats you want to order.">
            <IconNewOrder />
          </ModalHeader>
          <div className={styles.modalContent}>
            <div className={styles.orderDetails}>
              <div className={styles.orderDetailsRow}>
                <div className={styles.orderDetailsItem}>
                  <span className={styles.orderDetailsLabel}>Contractor ID</span>
                  <span className={styles.orderDetailsValue}>{selectedContractor && selectedContractor[0].value}</span>
                </div>
                <div className={styles.orderDetailsItem}>
                  <span className={styles.orderDetailsLabel}>Creation date</span>
                  <span className={styles.orderDetailsValue}>{dayjs().format("MM-DD-YYYY")}</span>
                </div>
              </div>
              <div className={styles.orderDetailsRow}>
                <div className={styles.orderDetailsItem}>
                  <span className={styles.orderDetailsLabel}>Company Name</span>
                  <SelectDynamic
                    values={selectedContractor || []}
                    valueName="id"
                    labelName="name"
                    name="contractors"
                    useLazyQuery={useLazyGetContractorsQuery}
                    placeholder="Company name"
                    onChange={setSelectedContractor}
                    className=""
                    queryParams={{ isEnabled: 1 }}
                  />
                </div>
                <div className={styles.orderDetailsItem}>
                  <span className={styles.orderDetailsLabel}>Warehouse</span>
                  <SelectDynamic
                    values={selectedWarehouse || []}
                    valueName="id"
                    labelName="name"
                    name="warehouses"
                    useLazyQuery={useLazyGetWarehouseListQuery}
                    placeholder="Warehouse"
                    onChange={setSelectedWarehouse}
                    className=""
                  />
                </div>
              </div>
            </div>
            {quantity.map((quant, index) => (
              <React.Fragment key={uuidv4()}>
                <div className={styles.separator} />
                <div className={styles.deviceWrapper}>
                  <div className={styles.deviceQtyWrapper}>
                    <span className={styles.deviceName}>Nuve Thermostats</span>
                    <span className={styles.deviceQty}>Qty: {quant}</span>
                  </div>
                  <InputNumber
                    minValue={1}
                    onInputChange={value => {
                      onInputChange(index, value)
                    }}
                    value={quant}
                  />

                  {quantity.length > 1 && (
                    <span
                      className={styles.deviceQtyDelete}
                      onClick={() => {
                        const temp = [...quantity]
                        temp.splice(index, 1)
                        setQuantity(temp)
                      }}
                    >
                      <IconDelete />
                    </span>
                  )}
                </div>
              </React.Fragment>
            ))}

            <div className={styles.separator} />
            <div>
              <ButtonNew
                color="blue"
                onClick={() => {
                  const temp = [...quantity]
                  temp.push(1)
                  setQuantity(temp)
                }}
              >
                Add new
              </ButtonNew>
            </div>
          </div>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
